import type { ComponentProps, Ref } from 'react';
import {
  type PressableProps,
  type PressableStateCallbackType,
  Pressable as RNPressable,
  type StyleProp,
  type View,
  type ViewStyle,
} from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import Box from '@/components/UI/Box';
import Text from '@/components/UI/Text';
import UnistylesIcon from '../Unistyles/Icon';

type PlaceholderProps = {
  icon: ComponentProps<typeof Icon>['name'];
  onPress?(): void;
  ref?: Ref<View>;
  subText?: string;
  text: string;
};

type PlaceholderPressableProps = PressableProps & {
  ref?: Ref<View>;
};

export function Container({
  style: extraStyles,
  ...props
}: ComponentProps<typeof Box>) {
  return <Box {...props} style={[styles.container, extraStyles]} />;
}

export function Icon(props: ComponentProps<typeof UnistylesIcon>) {
  return <UnistylesIcon size="9xl" variant="typographySecondary" {...props} />;
}

export function Pressable({
  style: extraStyles,
  ...props
}: PlaceholderPressableProps) {
  return (
    <RNPressable
      {...props}
      style={(state) => [
        styles.pressable,
        resolvePressableStyle(extraStyles, state),
      ]}
    />
  );
}

export function PlaceholderText({
  center = true,
  size = 'xl',
  variant = 'typographySecondary',
  ...props
}: ComponentProps<typeof Text>) {
  return <Text {...props} center={center} size={size} variant={variant} />;
}

export { PlaceholderText as Text };

export default function Placeholder({
  text,
  subText,
  icon,
  onPress,
  ref,
}: PlaceholderProps) {
  return (
    <Pressable ref={ref} onPress={onPress}>
      <Container flex={1}>
        <Icon size="9xl" name={icon} />
        <PlaceholderText>{text}</PlaceholderText>
        <PlaceholderText>{subText}</PlaceholderText>
      </Container>
    </Pressable>
  );
}

function resolvePressableStyle(
  style: PlaceholderPressableProps['style'],
  state: PressableStateCallbackType,
): StyleProp<ViewStyle> {
  if (typeof style === 'function') {
    return style(state);
  }

  return style;
}

const styles = StyleSheet.create((theme) => ({
  container: {
    alignItems: 'center',
    alignSelf: 'center',
    flex: 1,
    gap: theme.space('md'),
    justifyContent: 'center',
    paddingHorizontal: theme.space(4),
  },
  pressable: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
  },
}));
